import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

import { IGCPChatApiResponse } from 'src/interfaces/gcp-api-response';

// Function to determine which day's schedule to return based on current time and timezone
export const getAvailableSchedule = (
  schedules: Array<{ weekday: string; startTime: string; endTime: string }>,
  timezone?: string, // Optional timezone parameter (e.g., 'America/New_York', 'Europe/London')
): { weekday: string; startTime: string; endTime: string } | undefined => {
  try {
    // TODO Make the days localized
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Get current date/time in the specified timezone or use local timezone if not specified
    let now: Date;

    if (timezone) {
      try {
        // Test if the timezone is valid
        now = new Date(new Date().toLocaleString('en-US', { timeZone: timezone }));
        // If the date is invalid (NaN), throw an error to be caught
        if (isNaN(now.getTime())) {
          throw new Error('Invalid timezone');
        }
      } catch (error) {
        // If timezone is invalid, log error and fall back to local timezone
        console.error(`Invalid timezone: ${timezone}. Using local timezone instead.`, error);
        now = new Date();
      }
    } else {
      now = new Date();
    }

    const currentDayIndex = now.getDay(); // 0 for Sunday, 1 for Monday, etc.

    // Convert current time to minutes for comparison
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHours * 60 + currentMinutes;

    // Check if current day's start time is in the future
    const currentDaySchedule = schedules.find((s) => s.weekday === weekdays[currentDayIndex]);

    if (currentDaySchedule && currentDaySchedule.startTime && currentDaySchedule.startTime !== '-') {
      // Safely parse the time with a regex match that won't throw if the pattern doesn't match
      const timeMatch = currentDaySchedule.startTime.match(/(\d+):(\d+)\s*([AP]M)/);
      if (timeMatch) {
        const [, hours, minutes, period] = timeMatch;
        const startHours = parseInt(hours) + (period === 'PM' && parseInt(hours) !== 12 ? 12 : 0) - (period === 'AM' && parseInt(hours) === 12 ? 12 : 0);
        const startTimeInMinutes = startHours * 60 + parseInt(minutes);

        if (currentTimeInMinutes < startTimeInMinutes) {
          return currentDaySchedule;
        }
      }
    }

    // Look for the next available day
    for (let i = 1; i <= 7; i++) {
      const nextDayIndex = (currentDayIndex + i) % 7;
      const nextDaySchedule = schedules.find((s) => s.weekday === weekdays[nextDayIndex]);

      if (nextDaySchedule && nextDaySchedule.startTime && nextDaySchedule.startTime !== '-') {
        return nextDaySchedule;
      }
    }

    // Fallback if no valid schedule is found
    return schedules[0];
  } catch (error) {
    console.error('Error getting available schedule:', error);
  }
};

export const isWithinBusinessHours = (data: IGCPChatApiResponse): boolean => {
  if (!data) return false;
  if (data.status.isChatEnabled) return true;

  const timezoneName = data.timezone.name || 'America/New_York';

  // Get current time in the target timezone
  const currentTime = dayjs();
  const day = currentTime.day();

  let startTime = '';
  let endTime = '';

  switch (day) {
    case 0:
      startTime = data.schedule.SundayStartTime;
      endTime = data.schedule.SundayEndTime;
      break;
    case 1:
      startTime = data.schedule.MondayStartTime;
      endTime = data.schedule.MondayEndTime;
      break;
    case 2:
      startTime = data.schedule.TuesdayStartTime;
      endTime = data.schedule.TuesdayEndTime;
      break;
    case 3:
      startTime = data.schedule.WednesdayStartTime;
      endTime = data.schedule.WednesdayEndTime;
      break;
    case 4:
      startTime = data.schedule.ThursdayStartTime;
      endTime = data.schedule.ThursdayEndTime;
      break;
    case 5:
      startTime = data.schedule.FridayStartTime;
      endTime = data.schedule.FridayEndTime;
      break;
    case 6:
      startTime = data.schedule.SaturdayStartTime;
      endTime = data.schedule.SaturdayEndTime;
      break;
    default:
      break;
  }

  // If either start or end time is empty/undefined, consider it as closed
  if (!startTime || !endTime) {
    return false;
  }

  try {
    // Parse the times - format is "23:00:00.000Z"
    const startTimeStr = startTime.replace('.000Z', '');
    const endTimeStr = endTime.replace('.000Z', '');

    // Get today's date in the target timezone
    const todayInTimezone = currentTime.format('YYYY-MM-DD');

    // Create start and end time objects for today in the specified timezone
    const todayStart = dayjs.tz(`${todayInTimezone}T${startTimeStr}`, timezoneName);
    const todayEnd = dayjs.tz(`${todayInTimezone}T${endTimeStr}`, timezoneName);
    const updatedCurrentTime = dayjs.tz(currentTime.format(), timezoneName);

    // Handle case where end time is before start time (spans midnight)
    let adjustedEndTime = todayEnd;
    if (todayEnd.isBefore(todayStart)) {
      // End time is next day - add 1 day in the same timezone
      adjustedEndTime = todayEnd.add(1, 'day');
    }

    // Check if current time is within business hours
    // All comparisons are in the same timezone (timezoneName)
    const isWithinHours = (updatedCurrentTime.isAfter(todayStart) || updatedCurrentTime.isSame(todayStart)) && updatedCurrentTime.isBefore(adjustedEndTime);

    return isWithinHours;
  } catch (error) {
    console.error('Error parsing business hours:', error);
    return false;
  }
};
